import { useTranslation } from 'react-i18next';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../../components/AnimatedSection';

export default function InfrastructurePage() {
  const { t } = useTranslation();

  const infraServices = [
    {
      icon: 'ri-global-line',
      title: t('infra_s1_title'),
      desc: t('infra_s1_desc'),
      features: [t('infra_s1_f1'), t('infra_s1_f2'), t('infra_s1_f3'), t('infra_s1_f4')],
    },
    {
      icon: 'ri-wifi-line',
      title: t('infra_s2_title'),
      desc: t('infra_s2_desc'),
      features: [t('infra_s2_f1'), t('infra_s2_f2'), t('infra_s2_f3'), t('infra_s2_f4')],
    },
    {
      icon: 'ri-server-line',
      title: t('infra_s3_title'),
      desc: t('infra_s3_desc'),
      features: [t('infra_s3_f1'), t('infra_s3_f2'), t('infra_s3_f3'), t('infra_s3_f4')],
    },
    {
      icon: 'ri-git-branch-line',
      title: t('infra_s4_title'),
      desc: t('infra_s4_desc'),
      features: [t('infra_s4_f1'), t('infra_s4_f2'), t('infra_s4_f3'), t('infra_s4_f4')],
    },
  ];

  const problems = [
    { icon: 'ri-error-warning-line', text: t('infra_pain1'), color: 'text-red-400' },
    { icon: 'ri-speed-line', text: t('infra_pain2'), color: 'text-orange-400' },
    { icon: 'ri-building-3-line', text: t('infra_pain3'), color: 'text-yellow-400' },
    { icon: 'ri-timer-line', text: t('infra_pain4'), color: 'text-red-400' },
    { icon: 'ri-expand-left-right-line', text: t('infra_pain5'), color: 'text-orange-400' },
    { icon: 'ri-eye-off-line', text: t('infra_pain6'), color: 'text-yellow-400' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/de6e6cea49f30cd8bfa4f84c8cf6c12c.jpg"
            alt=""
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-in">
            <nav className="flex items-center gap-2 text-slate-400 text-sm mb-8">
              <Link to="/" className="hover:text-cyan-400 cursor-pointer">{t('infra_breadcrumb_home')}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">{t('infra_breadcrumb_current')}</span>
            </nav>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-full mb-6">
              <i className="ri-router-line text-blue-400 text-sm"></i>
              <span className="text-blue-400 text-sm font-medium">{t('infra_badge')}</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
              {t('infra_title1')}<br />
              <span className="text-blue-400">{t('infra_title2')}</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl leading-relaxed mb-8">
              {t('infra_desc')}
            </p>
            <div className="flex gap-4">
              <Link to="/contact" className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-6 py-3 rounded-lg transition-all whitespace-nowrap cursor-pointer">
                {t('infra_cta1')}
              </Link>
              <Link to="/case-studies" className="border border-slate-600 hover:border-blue-500 text-white hover:text-blue-400 px-6 py-3 rounded-lg transition-all whitespace-nowrap cursor-pointer">
                {t('infra_cta2')}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-in">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-3">{t('infra_pain_title')}</h2>
              <p className="text-slate-400">{t('infra_pain_sub')}</p>
            </div>
          </AnimatedSection>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {problems.map((p, i) => (
              <AnimatedSection key={i} animation="fade-in" delay={i * 0.05}>
                <div className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className={`${p.icon} text-xl ${p.color}`}></i>
                  </div>
                  <span className="text-slate-300 text-sm">{p.text}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection animation="fade-in">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-white mb-3">{t('infra_svc_title')}</h2>
              <p className="text-slate-400">{t('infra_svc_sub')}</p>
            </div>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-8">
            {infraServices.map((svc, i) => (
              <AnimatedSection key={svc.title} animation="fade-in" delay={i * 0.1}>
                <div className="bg-slate-800/50 border border-slate-700 hover:border-blue-500/40 rounded-2xl p-8 transition-all">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-500/10 rounded-xl mb-5">
                    <i className={`${svc.icon} text-blue-400 text-xl`}></i>
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{svc.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5">{svc.desc}</p>
                  <ul className="grid grid-cols-2 gap-2">
                    {svc.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-slate-300 text-xs">
                        <i className="ri-checkbox-circle-line text-blue-400"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900 border-t border-slate-800">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t('infra_cta_title')}</h2>
          <p className="text-slate-400 mb-8">{t('infra_cta_desc')}</p>
          <Link to="/contact" className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer">
            {t('infra_cta_btn')}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
